import {
  absentReasonText,
  conductAxisMeta,
  conductValueLabel,
  originSentence,
} from './conduct-labels';

import type {
  ConductAxis,
  ConductAxisResolution,
  ConductLevelPresence,
  ConductOriginLevel,
} from './conduct-types';

/**
 * Fase 2 «Conduta e Contexto» — UM eixo de conduta, num nível qualquer da cascata.
 *
 * ── O problema de UX que este componente existe para resolver ─────────────────────────
 * As 15 colunas são nullable e `null` NÃO é «vazio»: é «este nível não opinou». Um seletor
 * em branco tornaria indistinguíveis duas coisas opostas — «não configurei» e «configurei
 * com o valor que por acaso é o default». Essa confusão é a classe de erro que esta frente
 * inteira existe para matar.
 *
 * Então cada eixo mostra SEMPRE três coisas:
 *   1. **o valor efetivo** — o que a IA faz hoje, nunca um campo em branco;
 *   2. **de onde ele vem** — «Definido aqui» ou «Herdado do Tenant: Casa Aurora»;
 *   3. **o que muda se eu opinar aqui** — a opção «Herdar (…)» é a primeira do seletor e
 *      nomeia o valor que a herança daria, e escolhê-la SOLTA a opinião deste nível.
 *
 * ── «Soltar» é uma operação de primeira classe ────────────────────────────────────────
 * Escolher «Herdar» envia `null` na coluna — diferente de escolher o valor que a herança
 * daria por acaso. Sem isso a tela só saberia apertar, nunca soltar. Isto é alcançável
 * porque os 3 PATCHes aceitam `null` nas 5 colunas (verificado ao vivo: 200 + volta a
 * herdar). ⚠️ Contraste deliberado com o seletor de Persona do `TabGeneralEdit`, onde
 * «— nenhuma —» é `disabled` porque `personaId` é `string?` SEM `| null` no DTO.
 *
 * ── Sobreposição visível ──────────────────────────────────────────────────────────────
 * Quando este nível opinou e PERDEU para um mais específico, o componente avisa. Sem isso o
 * operador escolheria «Curtas» no playbook, veria «Curtas» na tela, e a IA responderia
 * longo por causa da persona — «a IA ignorou minha configuração», exatamente o bug que a
 * resolução server-side foi feita para não reproduzir.
 */

export interface ConductAxisFieldProps {
  readonly axis: ConductAxis;
  /** Em que nível esta tela está editando. Decide o texto de origem e quem sobrepõe quem. */
  readonly level: ConductOriginLevel;
  /** A resolução deste eixo, vinda de `GET /v1/conduct-profile/effective`. */
  readonly resolution: ConductAxisResolution | null;
  /** A opinião ATUAL deste nível (o rascunho do editor). `null` = herda. */
  readonly value: string | null;
  /** `null` solta a opinião (volta a herdar). */
  readonly onChange: (next: string | null) => void;
  readonly disabled?: boolean | undefined;
  /** Presença dos níveis, para explicar ausência com motivo. */
  readonly levels?: ConductProfileLevels | undefined;
}

export interface ConductProfileLevels {
  readonly persona: ConductLevelPresence;
  readonly playbook: ConductLevelPresence;
  readonly tenant: ConductLevelPresence;
}

/** Do mais específico ao menos — usado para saber quem sobrepõe este nível. */
const SPECIFICITY: readonly ConductOriginLevel[] = ['persona', 'playbook', 'tenant', 'platform'];

function isMoreSpecific(a: ConductOriginLevel, b: ConductOriginLevel): boolean {
  return SPECIFICITY.indexOf(a) < SPECIFICITY.indexOf(b);
}

/**
 * O valor que a herança daria se ESTE nível soltasse — lido das opinions da resolução, não
 * recalculado: quem decide precedência é o Engine.
 */
function inheritedValue(
  resolution: ConductAxisResolution | null,
  level: ConductOriginLevel,
): { value: string; from: ConductOriginLevel } | null {
  if (resolution === null) return null;
  const below = SPECIFICITY.slice(SPECIFICITY.indexOf(level) + 1);
  for (const lvl of below) {
    if (lvl === 'platform') return { value: resolution.opinions.platform, from: 'platform' };
    const opinion = resolution.opinions[lvl];
    if (opinion !== null) return { value: opinion, from: lvl };
  }
  return null;
}

export function ConductAxisField({
  axis,
  level,
  resolution,
  value,
  onChange,
  disabled = false,
  levels,
}: ConductAxisFieldProps): JSX.Element {
  const meta = conductAxisMeta(axis);
  const inherited = inheritedValue(resolution, level);
  const selectId = `conduct-${axis}`;

  // O nome do nível de origem, para a frase poder dizer «Herdado do Playbook: Suporte».
  const originName =
    resolution === null || levels === undefined || resolution.origin === 'platform'
      ? null
      : (levels[resolution.origin]?.name ?? null);

  // O nome do nível para onde este eixo CAI se soltar (≠ origem atual no estado sujo).
  const inheritedName =
    inherited === null || levels === undefined || inherited.from === 'platform'
      ? null
      : (levels[inherited.from]?.name ?? null);

  // Este nível opinou e perdeu? (a resolução aponta um nível MAIS específico)
  const overriddenBy =
    resolution !== null && value !== null && isMoreSpecific(resolution.origin, level)
      ? resolution.origin
      : null;
  const overriddenByName =
    overriddenBy === null || levels === undefined
      ? null
      : (levels[overriddenBy as keyof ConductProfileLevels]?.name ?? null);

  // Um nível existe no banco e o motor NÃO o consulta? (playbook draft, persona inativa,
  // nível persona atrás do portão do plano)
  const selfPresence = levels !== undefined && level !== 'platform' ? levels[level] : undefined;
  const inertReason =
    selfPresence !== undefined && !selfPresence.present
      ? absentReasonText(selfPresence.absentReason)
      : null;

  return (
    <div className="rounded-xl border border-border-subtle bg-surface p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-text-primary">{meta.label}</h3>
        {/* O valor EFETIVO, sempre visível — nunca um campo em branco. */}
        {resolution !== null ? (
          <span
            className="shrink-0 rounded-md bg-app-bg px-2 py-0.5 text-xs text-text-secondary"
            data-testid={`conduct-effective-${axis}`}
          >
            Hoje:{' '}
            <strong className="text-text-primary">
              {conductValueLabel(axis, resolution.value)}
            </strong>
          </span>
        ) : null}
      </div>

      <p className="mt-1 text-xs text-text-secondary">{meta.question}</p>

      <select
        id={selectId}
        aria-label={meta.label}
        className="mt-3 w-full rounded-lg border border-border-subtle bg-app-bg px-3 py-2 text-sm text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => {
          // '' é a opção «Herdar» ⇒ null ⇒ SOLTA a opinião deste nível.
          onChange(e.target.value === '' ? null : e.target.value);
        }}
      >
        {/*
          A opção de HERDAR é a primeira e NOMEIA o valor que a herança daria. Habilitada —
          é o caminho explícito de volta, e o PATCH expressa `null` nos 3 níveis.
        */}
        <option value="">
          {inherited === null ? 'Herdar' : `Herdar — ${conductValueLabel(axis, inherited.value)}`}
        </option>
        {meta.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/*
        De ONDE vem o valor.

        ★ Quando este nível NÃO opina, a origem é lida de `inherited` — que por construção
        olha só os níveis ABAIXO deste — e NÃO de `resolution.origin`. A diferença aparece
        no estado sujo, e foi um bug pego na validação viva: o usuário escolhia «Herdar»,
        o servidor ainda tinha a opinião deste nível, `resolution.origin` continuava
        apontando para cá, e a frase dizia «Definido aqui» ao lado de um seletor escrito
        «Herdar». Ler de `inherited` responde a pergunta certa — «para onde este eixo cai se
        eu soltar» — em vez da pergunta de ontem.
      */}
      {resolution !== null ? (
        <p className="mt-2 text-xs text-text-secondary" data-testid={`conduct-origin-${axis}`}>
          {value !== null
            ? 'Definido aqui, neste nível.'
            : inherited === null
              ? originSentence(resolution.origin, level, originName)
              : originSentence(
                  inherited.from,
                  level,
                  inherited.from === 'platform' ? null : inheritedName,
                )}
        </p>
      ) : null}

      {/* O que a opção escolhida MUDA na fala. */}
      {value !== null ? (
        <p className="mt-1 text-xs text-text-secondary">
          {meta.options.find((o) => o.value === value)?.desc ?? ''}
        </p>
      ) : null}

      {/* Este nível opinou e PERDEU — sem este aviso, a tela mentiria por omissão. */}
      {overriddenBy !== null ? (
        <p
          className="mt-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
          data-testid={`conduct-overridden-${axis}`}
        >
          Sua escolha aqui está sendo sobreposta pela{' '}
          {overriddenBy === 'persona' ? 'Persona' : 'Playbook'}
          {overriddenByName === null ? '' : `: ${overriddenByName}`} — que é mais específica. A IA
          usa <strong>{conductValueLabel(axis, resolution?.value ?? null)}</strong> hoje.
        </p>
      ) : null}

      {/* O nível existe e o motor não o lê. */}
      {inertReason !== null ? (
        <p
          className="mt-2 rounded-lg bg-app-bg px-3 py-2 text-xs text-text-secondary"
          data-testid={`conduct-inert-${axis}`}
        >
          {inertReason}
        </p>
      ) : null}
    </div>
  );
}
